import React, { useState, forwardRef, useEffect } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { TimelineContainer } from './timeline/TimelineContainer';
import { AudioEngine } from '../services/AudioEngine';

interface TimelinePanelProps {
  snapLine?: number | null;
  isValidDrop?: boolean;
}

export const TimelinePanel = forwardRef<HTMLDivElement, TimelinePanelProps>(({
  snapLine = null,
  isValidDrop = true
}, ref) => {
  // Connect to store
  const tracks = useProjectStore((state) => state.tracks);
  const clips = useProjectStore((state) => state.clips);
  const assets = useProjectStore((state) => state.assets);
  const trackOrder = useProjectStore((state) => state.trackOrder);
  const markers = useProjectStore((state) => state.markers);

  // Actions
  const moveClip = useProjectStore((state) => state.moveClip);
  const resizeClip = useProjectStore((state) => state.resizeClip);
  const duplicateClip = useProjectStore((state) => state.duplicateClip);
  const splitClip = useProjectStore((state) => state.splitClip);
  const rippleDeleteClip = useProjectStore((state) => state.rippleDeleteClip);
  const addTransition = useProjectStore((state) => state.addTransition);
  const removeClip = useProjectStore((state) => state.removeClip);
  const addTrack = useProjectStore((state) => state.addTrack);
  const removeTrack = useProjectStore((state) => state.removeTrack);
  const updateTrack = useProjectStore((state) => state.updateTrack);
  const setTrackViewMode = useProjectStore((state) => state.setTrackViewMode);
  const addMarker = useProjectStore((state) => state.addMarker);
  const selectClip = useProjectStore((state) => state.selectClip);
  const setSettings = useProjectStore((state) => state.setSettings);
  const setZoomLevel = useProjectStore((state) => state.setZoomLevel);

  // State
  const currentTime = useProjectStore((state) => state.currentTime);
  const isPlaying = useProjectStore((state) => state.isPlaying);
  const selectedClipId = useProjectStore((state) => state.selectedClipId);
  const settings = useProjectStore((state) => state.settings);
  const setPlaybackState = useProjectStore((state) => state.setPlaybackState);
  const zoomLevel = useProjectStore((state) => state.zoomLevel);

  // Sync Audio Engine Tracks
  useEffect(() => {
    const engine = AudioEngine.getInstance();
    Object.values(tracks).forEach(track => {
        engine.registerTrack(track.id, track.volume ?? 1.0, track.isMuted);
    });
  }, [tracks]);

  const handleMarkerClick = (id: string) => {
    const marker = markers.find((m) => m.id === id);
    if (marker) {
      setPlaybackState({ currentTime: marker.time });
    }
  };

  const handleAddMarker = () => {
    addMarker({
      id: `marker-${Date.now()}`,
      time: currentTime,
      label: 'M',
      color: '#f59e0b',
    });
  };

  const handleAddTrack = () => {
    const id = `track-${Date.now()}`;
    addTrack({
      id,
      type: 'video',
      label: `Track ${trackOrder.length + 1}`,
      isMuted: false,
      isLocked: false,
      volume: 1.0,
      clips: [],
    });
  };

  const handleToggleMute = (id: string) => {
    const track = tracks[id];
    if (track) {
      updateTrack(id, { isMuted: !track.isMuted });
    }
  };

  const handleToggleLock = (id: string) => {
    const track = tracks[id];
    if (track) {
      updateTrack(id, { isLocked: !track.isLocked });
    }
  };

  const handleUpdateVolume = (id: string, volume: number) => {
      updateTrack(id, { volume });
  };

  const handleToggleTrackViewMode = (id: string) => {
    const track = tracks[id];
    if (track && track.type === 'video') {
       const currentMode = track.viewMode || 'frames';
       const nextMode = currentMode === 'frames' ? 'waveform' : currentMode === 'waveform' ? 'both' : 'frames';
       setTrackViewMode(id, nextMode);
    }
  };

  return (
    <div className="h-full w-full bg-gray-900 text-white overflow-hidden">
      <TimelineContainer
        ref={ref}
        tracks={tracks}
        clips={clips}
        assets={assets}
        trackOrder={trackOrder}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        snapToGrid={settings.snapToGrid ?? true}
        allowOverlaps={settings.allowOverlaps ?? false}
        setSettings={setSettings}
        onMoveClip={moveClip}
        onResizeClip={resizeClip}
        onDuplicateClip={duplicateClip}
        onSplitClip={splitClip}
        onRippleDeleteClip={rippleDeleteClip}
        onAddTransition={addTransition}
        onRemoveClip={removeClip}
        onRemoveTrack={removeTrack}
        onToggleTrackMute={handleToggleMute}
        onToggleTrackLock={handleToggleLock}
        onUpdateTrackVolume={handleUpdateVolume}
        onToggleTrackViewMode={handleToggleTrackViewMode}
        onAddTrack={handleAddTrack}
        onAddMarker={handleAddMarker}
        selectedClipId={selectedClipId}
        onClipSelect={selectClip}
        currentTime={currentTime}
        isPlaying={isPlaying}
        markers={markers}
        onMarkerClick={handleMarkerClick}
        externalSnapLine={snapLine}
        externalIsValidDrop={isValidDrop}
      />
    </div>
  );
});

TimelinePanel.displayName = 'TimelinePanel';
